// React
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";

// MUI
import { GRID_CHECKBOX_SELECTION_COL_DEF, GridCellParams, GridRowId, GridRowSelectionModel, useGridApiRef } from "@mui/x-data-grid-premium";
import { gridExpandedSortedRowIdsSelector } from "@mui/x-data-grid-pro";
import { SxProps } from "@mui/material";

// Third Party
import { debounce } from "lodash-es";

// Hooks
import useAllDealsTableColumns from "@DataPages/hooks/financeManagement/allDeals/tables/useAllDealsTableColumns";
import { useAllDealsContextData } from "@DataPages/context/cit/AllDealsContext";
import { useLocation } from "@Common/hooks/Location/useLocation";

// Utils
import { generateSx, getRowClassName } from "@Layouts/components/Tables/DataSmartTable/helpers";
import { isFooterCell } from "@Washouts/helpers/tables.ts";

// Components
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import { CitDataTableTitle } from "@DataPages/components/FniManagement/components/CIT/CitDataTableTitle";

// Constants
import { DEFAULT_HEADER_HEIGHT, DEFAULT_ROW_HEIGHT } from "@Washouts/constants";
import { tableKeys } from "@Constants/tableKeys";

// Types
import { IDeal } from "@Common/types/MyCit/model";

type TAllDealsTable = {
	allDeals: IDeal[];
	hasNextPage: boolean;
	isFetching: boolean;
};

// Table Constants
const pinnedColumns = { left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, "funded", "date", "dealNumber", "balance", "__empty"] };
const aggregationModel = {
	dealNumber: "size",
	balance: "sum",
	transactionAmount: "sum",
	amountFinanced: "sum",
};
const columnVisibilityModel = {
	_id: false,
};

function getCellClassName(params: GridCellParams) {
	// Targeting footer
	if (!isFooterCell(params)) {
		return "";
	}

	// Use this to change the cell class name depending on the cell value
	const negatives = ["amountFinanced"];
	if (negatives.includes(params.field) && (params.value as number) < 0) {
		return "aggregation-footer-negative";
	}

	// Append the label 'Total' to the cell value in the footer
	if (params.field === "date") {
		return "aggregation-footer-total";
	}

	if (params.field === "dealNumber") {
		return "aggregation-footer-negative";
	}

	return "";
}

export default function AllDealsTable(props: TAllDealsTable) {
	const { allDeals, isFetching } = props;

	const location = useLocation();
	const { setFilteredAllDeals } = useAllDealsContextData();
	const apiRef = useGridApiRef();

	const { columns } = useAllDealsTableColumns({ location, onRowSelection: handleRowSelection });

	// Local States
	const [sx, setSx] = useState<SxProps>({});
	const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);

	/********************************************* HANDLERS ************************************************/
	function handleRowSelection(event: ChangeEvent<HTMLInputElement>, id: GridRowId) {
		const checked = event.target.checked;
		if (checked) {
			setSelectedRows((prevSelectedRows) => [...prevSelectedRows, id]);
		} else {
			setSelectedRows((prevSelectedRows) => prevSelectedRows.filter((itemId) => itemId !== id));
		}
	}

	const debouncedSetFilteredAllDeals = useRef(
		debounce((rowIds: GridRowId[]) => {
			setFilteredAllDeals(rowIds);
		}, 300)
	).current;

	const onStateChange = useCallback(() => {
		if (!apiRef.current) {
			return;
		}

		const expandedSortedRowIds = gridExpandedSortedRowIdsSelector(apiRef);

		debouncedSetFilteredAllDeals(expandedSortedRowIds);
	}, [debouncedSetFilteredAllDeals]);

	useEffect(() => {
		if (!apiRef.current?.subscribeEvent) {
			return () => {};
		}

		const clearListener1 = apiRef.current?.subscribeEvent("filterModelChange", onStateChange);
		const clearListener2 = apiRef.current?.subscribeEvent("rowCountChange", onStateChange);

		onStateChange();

		return () => {
			clearListener1();
			clearListener2();
			// Cancel any pending debounced calls
			debouncedSetFilteredAllDeals.cancel();
		};
	}, [allDeals, onStateChange, debouncedSetFilteredAllDeals]);

	useEffect(() => {
		if (apiRef) {
			setSx(generateSx(apiRef, { yGap: "0px" }));
		}

		return apiRef.current?.subscribeEvent("virtualScrollerContentSizeChange", () => {
			setSx(generateSx(apiRef));
		});
	}, [apiRef]);

	return (
		<DataSmartTable
			apiRef={apiRef}
			filterStoreConfig={{
				key: tableKeys.DATA_PAGES.FNI_MANAGEMENT_ALL_DEALS,
			}}
			// Data
			columns={columns}
			rows={allDeals ?? []}
			rowSelectionModel={selectedRows}
			// Behavior
			hideFooter
			enableCustomScrollbars
			disableColumnMenu
			checkboxSelection
			disableRowSelectionOnClick
			aggregationModel={aggregationModel}
			pinnedColumns={pinnedColumns}
			columnVisibilityModel={columnVisibilityModel}
			getCellClassName={getCellClassName}
			// Styles
			maxRows={15}
			rowHeight={DEFAULT_ROW_HEIGHT}
			headerHeight={DEFAULT_HEADER_HEIGHT}
			getRowClassName={getRowClassName}
			sx={sx}
			slots={{
				toolbar: CommonToolbar,
				noRowsOverlay: NoRowsOverlay,
			}}
			slotProps={{
				toolbar: {
					titleExpandible: {
						className: "mt-[8px] [&_.toolbar-actions]:!right-0 [&_.toolbar-actions]:!bottom-0",
						hide: true,
					},
					customComponent: <CitDataTableTitle isFetching={isFetching} source={"All Deals"} />,
					filter: {
						disabled: false,
					},
					search: {
						useQuickFilter: true,
					},
				},
				noRowsOverlay: {
					title: "No Deals Available",
				},
			}}
			customProps={{
				tableClassName: {
					"functional-table": true,
					"[&_.MuiDataGrid-columnHeader--withRightBorder]:!border-none": true,
					"[&_.MuiDataGrid-filler--pinnedLeft]:!border-r-transparent": true,
					// Prevent hover effect on footer row - maintain primary background
					"[&_.MuiDataGrid-row.footer:hover]:!bg-primary": true,
					"[&_.MuiDataGrid-row.footer:hover_.MuiDataGrid-cell:not(.empty-row)]:!bg-primary": true,
					// Keep empty-row cells white even on footer hover
					"[&_.MuiDataGrid-row.footer:hover_.MuiDataGrid-cell.empty-row]:!bg-white": true,
					// Keep pinned cell shadow only for non-footer rows
					"[&_.MuiDataGrid-row:not(.footer):hover_.MuiDataGrid-cell--pinnedLeft:not(.empty-row,.group-child)]:shadow-table-hov":
						true,
				},
				expandableRows: true,
				hide: false,
			}}
		/>
	);
}
